import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button, TextField, Checkbox, FormControlLabel, Link, Typography, InputAdornment, IconButton, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { isWhitespaceOnlyPassword, normalizeSignupText, SIGNUP_WHITESPACE_PASSWORD_ERROR } from '../../../../utils/signupText';

interface IFormData {
    email: string;
    password: string;
    referralCode: string;
    terms: boolean;
}

interface SignUpFormProps {
    onSubmit: (data: IFormData) => void;
    apiError: string;
    referralCodeError?: string;
    // T2-5090: pre-fill the referral field when the user arrives via a referral share link
    // (`?ref=CODE`). Empty string when none was supplied.
    initialReferralCode?: string;
}

const PRIVACY_POLICY_PATH = 'privacy.html';

const SignUpForm: React.FC<SignUpFormProps> = ({ onSubmit, apiError, referralCodeError, initialReferralCode = '' }) => {
    const { control, handleSubmit, formState: { errors } } = useForm<IFormData>({
        defaultValues: {
            email: '',
            password: '',
            referralCode: initialReferralCode,
            terms: false,
        },
    });

    const [passwordHelpOpen, setPasswordHelpOpen] = React.useState(false);
    const handleTooltipOpen = () => setPasswordHelpOpen(true);
    const handleTooltipClose = () => setPasswordHelpOpen(false);
    const handleTooltipToggle = () => setPasswordHelpOpen((prev) => !prev);

    const openExternal = (infoLink: string) => {
        // @ts-ignore
        if (window.Cordova && window.cordova?.InAppBrowser?.open) {
            // @ts-ignore
            window.cordova.InAppBrowser.open(infoLink, '_system', 'location=yes');
        } else {
            window.open(infoLink, '_blank', 'location=yes');
        }
    };

    const handleExternalClick = (url: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        openExternal(url);
    };

    const handlePrivacyPolicyClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const inAppBrowser = window.cordova?.InAppBrowser;
        const applicationDirectory = window.cordova?.file?.applicationDirectory;

        if (inAppBrowser?.open && applicationDirectory) {
            // Ionic virtual origins resolve only in the main Cordova WebView. Use the
            // File plugin's native application root for the separate InAppBrowser.
            const nativeRoot = applicationDirectory.replace(/\/?$/, '/');
            inAppBrowser.open(`${nativeRoot}www/${PRIVACY_POLICY_PATH}`, '_blank', 'location=yes');
        } else if (window.cordova) {
            // The page is still reachable in the main WebView if the File bridge is late.
            window.location.assign(PRIVACY_POLICY_PATH);
        } else {
            window.open(PRIVACY_POLICY_PATH, '_blank', 'location=yes');
        }
    };

    return (
        <>
            <style>
                {`
                    .link {
                        color: #0B4598;
                        text-decoration: underline;
                        cursor: pointer;
                    }
                    .link:hover {
                        text-decoration: none;
                    }
                `}
            </style>
            <form data-testid="signup-main-form" onSubmit={handleSubmit(data => onSubmit({...data, referralCode: normalizeSignupText(data.referralCode)}))} style={{width: '100%'}}>
            {apiError && (
                <Typography 
                    color="error" 
                    sx={{ 
                        mb: 1, 
                        fontSize: '0.875rem',
                        textAlign: 'left',
                        padding: '0.5rem',
                        backgroundColor: '#ffebee',
                        borderRadius: '4px',
                        border: '1px solid #f44336'
                    }}
                >
                    {apiError}
                </Typography>
            )}
            <Controller
                name="email"
                control={control}
                rules={{ required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' } }}
                render={({ field }) => (
                    <TextField 
                        {...field} 
                        label="Email" 
                        variant="outlined" 
                        fullWidth 
                        sx={{ 
                            mb: 1,
                            '@media (max-width: 360px)': {
                                mb: 0.75,
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '0.875rem',
                                '@media (max-width: 360px)': { fontSize: '0.8rem' },
                                '@media (min-width: 768px)': { fontSize: '1rem' },
                                '@media (min-width: 901px)': { fontSize: '1rem' }
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '0.875rem',
                                padding: '10px 14px',
                                '@media (max-width: 360px)': { 
                                    fontSize: '0.8125rem',
                                    padding: '9px 12px'
                                },
                                '@media (min-width: 768px)': { 
                                    fontSize: '1rem',
                                    padding: '12px 14px'
                                },
                                '@media (min-width: 901px)': { 
                                    fontSize: '1rem',
                                    padding: '12px 16px'
                                }
                            },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px'
                            }
                        }} 
                        error={!!errors.email} 
                        helperText={errors.email?.message} 
                    />
                )}
            />
            <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required', minLength: { value: 8, message: 'Password must be at least 8 characters' }, validate: value => !isWhitespaceOnlyPassword(value) || SIGNUP_WHITESPACE_PASSWORD_ERROR }}
                render={({ field }) => (
                    <TextField 
                        {...field} 
                        label="Password (at least 8 characters)" 
                        type="password" 
                        variant="outlined" 
                        fullWidth 
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <ClickAwayListener onClickAway={handleTooltipClose}>
                                        <Tooltip 
                                            title="Password must be at least 8 characters long"
                                            open={passwordHelpOpen}
                                            onOpen={handleTooltipOpen}
                                            onClose={handleTooltipClose}
                                            placement="top"
                                        >
                                            <IconButton
                                                aria-label="password help"
                                                edge="end"
                                                size="small"
                                                onClick={handleTooltipToggle}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </ClickAwayListener>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ 
                            mb: 1,
                            '@media (max-width: 360px)': {
                                mb: 0.75,
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '0.875rem',
                                '@media (max-width: 360px)': { fontSize: '0.8rem' },
                                '@media (min-width: 768px)': { fontSize: '1rem' },
                                '@media (min-width: 901px)': { fontSize: '1rem' }
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '0.875rem',
                                padding: '10px 14px',
                                '@media (max-width: 360px)': { 
                                    fontSize: '0.8125rem',
                                    padding: '9px 12px'
                                },
                                '@media (min-width: 768px)': { 
                                    fontSize: '1rem',
                                    padding: '12px 14px'
                                },
                                '@media (min-width: 901px)': { 
                                    fontSize: '1rem',
                                    padding: '12px 16px'
                                }
                            },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px'
                            }
                        }} 
                        error={!!errors.password} 
                        helperText={errors.password?.message}
                    />
                )}
            />
            <Controller
                name="referralCode"
                control={control}
                render={({ field }) => (
                    <TextField 
                        {...field} 
                        label="Have a Referral Code?" 
                        variant="outlined" 
                        fullWidth 
                        error={!!referralCodeError}
                        helperText={referralCodeError}
                        sx={{ 
                            mb: 1,
                            '@media (max-width: 360px)': {
                                mb: 0.75,
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '0.875rem',
                                '@media (max-width: 360px)': { fontSize: '0.8rem' },
                                '@media (min-width: 768px)': { fontSize: '1rem' },
                                '@media (min-width: 901px)': { fontSize: '1rem' }
                            },
                            '& .MuiInputBase-input': {
                                fontSize: '0.875rem',
                                padding: '10px 14px',
                                '@media (max-width: 360px)': { 
                                    fontSize: '0.8125rem',
                                    padding: '9px 12px'
                                },
                                '@media (min-width: 768px)': { 
                                    fontSize: '1rem',
                                    padding: '12px 14px'
                                },
                                '@media (min-width: 901px)': { 
                                    fontSize: '1rem',
                                    padding: '12px 16px'
                                }
                            },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px'
                            }
                        }} 
                    />
                )}
            />

            <Controller
                name="terms"
                control={control}
                rules={{ required: 'You must accept the terms and conditions' }}
                render={({ field }) => (
                    <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} size="small" />}
                    label={
                        <Typography variant="body2" color={errors.terms ? 'error' : 'inherit'} sx={{
                            fontSize: '0.75rem',
                            lineHeight: 1.4,
                            '@media (max-width: 400px)': { fontSize: '0.7rem' },
                            '@media (max-width: 360px)': { fontSize: '0.6875rem' },
                            '@media (min-width: 768px)': { fontSize: '0.875rem' },
                            '@media (min-width: 901px)': { fontSize: '0.875rem' }
                        }}>
                        I confirm that I have read and agree to TempStars' <Link href="https://www.tempstars.ca/terms" onClick={handleExternalClick('https://www.tempstars.ca/terms')} className="link">Terms of Service</Link> and <Link href={PRIVACY_POLICY_PATH} onClick={handlePrivacyPolicyClick} className="link">Privacy Policy</Link>
                        </Typography>
                    }
                    sx={{ mb: 1, textAlign: 'left', alignItems: 'flex-start' }}
                    />
                )}
            />
             {errors.terms && (
                <Typography 
                    color="error" 
                    variant="caption" 
                    sx={{ 
                        fontSize: '0.75rem',
                        display: 'block',
                        textAlign: 'left',
                        mb: 0.5
                    }}
                >
                    {errors.terms.message}
                </Typography>
            )}

            <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large" 
                fullWidth 
                sx={{ 
                    mb: 1,
                    '@media (max-width: 360px)': { mb: 0.75, padding: '9px 20px', fontSize: '0.85rem' },
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    padding: '10px 24px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '@media (min-width: 768px)': { 
                        fontSize: '1rem',
                        padding: '12px 24px'
                    },
                    '@media (min-width: 901px)': { 
                        fontSize: '1rem',
                        padding: '14px 28px'
                    }
                }} 
                className="no-fastclick"
            >
            Sign Up
            </Button>
        </form>
        </>
    );
}

export default SignUpForm;
